'use client';

import { useState, useRef, useEffect } from 'react';

export default function CountUp({ value }: { value: string }) {
  const [display, setDisplay] = useState<string>(() =>
    format(0, parseValue(value).suffix)
  );
  const startedRef = useRef(false);
  const elRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!elRef.current || startedRef.current) return;

    function animate() {
      const { number, decimals, suffix } = parseValue(value);
      const prefersReduced =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = prefersReduced ? 0 : 1200;
      if (duration === 0) {
        setDisplay(format(number, suffix, decimals));
        return;
      }
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        const current = number * eased;
        setDisplay(format(current, suffix, decimals));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            animate();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(elRef.current);

    return () => observer.disconnect();
  }, [value]);

  return <span ref={elRef}>{display}</span>;
}

function parseValue(raw: string): {
  number: number;
  decimals: number;
  suffix: string;
} {
  const match = raw.match(/([0-9]+(?:\.[0-9]+)?)(.*)/);
  const num = match ? parseFloat(match[1]) : 0;
  const suffix = match ? match[2] : '';
  const decimals =
    match && match[1].includes('.') ? match[1].split('.')[1]?.length || 0 : 0;
  return { number: num, decimals, suffix };
}

function format(n: number, suffix: string, decimals = 0): string {
  const fixed = decimals > 0 ? n.toFixed(decimals) : Math.round(n).toString();
  return `${fixed}${suffix}`;
}
